/**
 * Session Expired Modal
 *
 * Displays a friendly modal when the user's session expires unexpectedly,
 * providing a clear path to re-authentication.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Clock } from 'lucide-react';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import { saveRedirectUrl } from '@/lib/storage/sessionRedirect';

interface SessionExpiredModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPublicPage?: boolean;
}

export function SessionExpiredModal({
  open,
  onOpenChange,
  isPublicPage = false,
}: SessionExpiredModalProps) {
  const { login } = useTurnkeyWallet();

  const handleSignIn = () => {
    // Save current URL for redirect after auth
    saveRedirectUrl(window.location.pathname + window.location.search);
    onOpenChange(false);
    // Open Turnkey login modal
    login();
  };

  const handleContinue = () => {
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="bg-[#111111] border-[#1A1A1A] max-w-[400px] w-[calc(100%-32px)] rounded-xl z-[100]"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <AlertDialogHeader className="items-center text-center">
          <div className="w-12 h-12 rounded-full bg-[#1A1A1A] flex items-center justify-center mb-2">
            <Clock className="w-6 h-6 text-[#888888]" />
          </div>
          <AlertDialogTitle className="text-white text-lg font-semibold">
            Session Expired
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[#888888] text-sm">
            For your security, your session has expired due to inactivity.
            Sign in again to continue trading.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col mt-2">
          <AlertDialogAction
            onClick={handleSignIn}
            className="w-full bg-[#CCFF00] text-black hover:bg-[#b8e600] font-medium h-11"
          >
            Sign In Again
          </AlertDialogAction>
          {isPublicPage && (
            <AlertDialogCancel
              onClick={handleContinue}
              className="w-full bg-transparent border-[#1A1A1A] text-white hover:bg-[#1A1A1A] h-11 mt-0"
            >
              Continue Browsing
            </AlertDialogCancel>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default SessionExpiredModal;
